/**
 * A snippet with the server's match spans marked.
 *
 * The offsets come from the server (`global_search.make_snippet`), not from a
 * second client-side match, so the result list highlights exactly what the
 * index scored. Spans that fall outside the text — a stale response racing a
 * newer query — are skipped rather than throwing.
 */

import type { CSSProperties, ReactNode } from 'react'
import type { MatchSpan } from './contracts'

export interface HighlightedTextProps {
  text: string
  matches?: MatchSpan[]
  /** Rendered before the text when the snippet is a window into a longer body. */
  truncatedStart?: boolean
  truncatedEnd?: boolean
  style?: CSSProperties
  markStyle?: CSSProperties
}

const DEFAULT_MARK: CSSProperties = {
  background: 'var(--color-warning-bg, #fde68a)',
  color: 'var(--color-warning-text, #111827)',
  borderRadius: 2,
  padding: '0 1px',
  fontWeight: 600,
}

export function HighlightedText({
  text, matches, truncatedStart, truncatedEnd, style, markStyle,
}: HighlightedTextProps) {
  const body = text || ''
  const spans = (matches || [])
    .filter(([s, e]) => Number.isFinite(s) && Number.isFinite(e)
      && s >= 0 && e <= body.length && e > s)
    .sort((a, b) => a[0] - b[0])

  const parts: ReactNode[] = []
  let cursor = 0
  spans.forEach(([start, end], i) => {
    if (start < cursor) return   // overlapping span from a stale response
    if (start > cursor) parts.push(body.slice(cursor, start))
    parts.push(
      <mark key={`m${i}`} style={{ ...DEFAULT_MARK, ...markStyle }}>
        {body.slice(start, end)}
      </mark>,
    )
    cursor = end
  })
  if (cursor < body.length) parts.push(body.slice(cursor))

  return (
    <span style={style}>
      {truncatedStart ? '…' : null}
      {parts}
      {truncatedEnd ? '…' : null}
    </span>
  )
}

export default HighlightedText
